import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError } from '../net/client';
import { OTP_LENGTH } from './otpCode';
import { bannerFor, type Refusal } from './refusals';

/** OtpEntry's resend cooldown, which is also the API's `OTP_RESEND_COOLDOWN_SECONDS` default. */
export const RESEND_SECONDS = 60;

export const wrongCode: Refusal = {
  message: 'That code is not right.',
  hint: 'Check it and try again.',
};

/** `0:42`, the resend countdown. */
export function countdown(total: number): string {
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * A mailed code being typed: what sign-in's OtpEntry and the API keys
 * step-up panel share. What the code is spent on is the caller's `submit`. A
 * refused code reads as not right, any other failure as its banner, and
 * Resend waits out the cooldown, the API's own count winning over this one.
 */
export function useOtpStep(submit: (code: string) => Promise<void>, send: () => Promise<void>) {
  const [code, setCodeRaw] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const inFlight = useRef(false);

  useEffect(() => {
    const tick = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(tick);
  }, []);

  const complete = code.length === OTP_LENGTH;

  function setCode(next: string) {
    setCodeRaw(next);
    setRefusal(null);
  }

  async function verify(event: FormEvent) {
    event.preventDefault();
    if (!complete || inFlight.current) return;
    inFlight.current = true;
    setVerifying(true);
    try {
      await submit(code);
    } catch (error) {
      const refused =
        error instanceof ApiError &&
        (error.code === 'invalid_code' || error.code === 'invalid_challenge');
      setRefusal(refused ? wrongCode : bannerFor(error));
    } finally {
      inFlight.current = false;
      setVerifying(false);
    }
  }

  function resend() {
    if (secondsLeft > 0) return;
    setSecondsLeft(RESEND_SECONDS);
    send().catch((error: unknown) => {
      if (error instanceof ApiError && error.kind === 'rate_limited') {
        setSecondsLeft(error.retryAfterSeconds ?? RESEND_SECONDS);
      } else {
        setRefusal(bannerFor(error));
      }
    });
  }

  return {
    code,
    setCode,
    verifying,
    refusal,
    secondsLeft,
    canVerify: complete && !verifying,
    verify,
    resend,
  };
}
